import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import Grid from "@material-ui/core/Grid"
import List from "@material-ui/core/List"
import Card from "@material-ui/core/Card"
import CardHeader from "@material-ui/core/CardHeader"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import Checkbox from "@material-ui/core/Checkbox"
import Button from "@material-ui/core/Button"
import Divider from "@material-ui/core/Divider"
import { CustomReportData } from "../Driftskontrakt/update"
import { VegobjektType } from "../../utils/vegobjekt"
import * as R from "remeda"
import tw from "twin.macro"
import { TextField } from "@material-ui/core"

const useStyles = makeStyles(theme => ({
  root: {
    margin: "auto",
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
    marginBottom: 0,
  },
  list: {
    width: 320,
    height: 500,
    backgroundColor: theme.palette.background.paper,
    overflow: "auto",
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}))

function not(a: VegobjektType[], b: VegobjektType[]) {
  return a.filter(value => b.indexOf(value) === -1)
}

function intersection(a: VegobjektType[], b: VegobjektType[]) {
  return a.filter(value => b.indexOf(value) !== -1)
}

function union(a: VegobjektType[], b: VegobjektType[]) {
  return [...a, ...not(b, a)]
}

interface TransferListProps {
  customReport: CustomReportData;
  updateRemovals: React.Dispatch<VegobjektType[]>;
  updateAdditions: React.Dispatch<VegobjektType[]>;
  resetSelections: () => void;
  title?: string;
}

export default function TransferList({
  customReport,
  updateRemovals,
  updateAdditions,
  resetSelections,
  title = "Tilvalg av objekttyper",
}: TransferListProps) {
  const classes = useStyles()
  const [checked, setChecked] = React.useState<VegobjektType[]>([])

  const left = R.sortBy(customReport.omit, objekt => objekt.navn)
  const right = R.sortBy(customReport.add, objekt => objekt.navn)

  const setLeft = (objects: VegobjektType[]) => updateRemovals(objects)
  // searchDispatch({ type: "UpdateCustomRemovals", payload: objects })

  const setRight = (objects: VegobjektType[]) => updateAdditions(objects)
  // searchDispatch({ type: "UpdateCustomAdditions", payload: objects })

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  const handleToggle = (value: VegobjektType) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const numberOfChecked = (items: VegobjektType[]) =>
    intersection(checked, items).length

  const handleToggleAll = (items: VegobjektType[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items))
    } else {
      setChecked(union(checked, items))
    }
  }

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked))
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked))
    setRight(not(right, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  return (
    <>
      <h3 style={tw`text-xl font-bold`}>{title}</h3>
      <p style={tw`leading-loose text-sm`}>
        Flytt objekttyper til høyre for å velge legge objekter til i rapporten. Flytt til venstre for å utelate.
      </p>
      <Grid
        container
        spacing={0}
        justify="space-between"
        alignItems="center"
        className={classes.root}
      >
        <Grid tw="border-solid border-red-200 border-2 rounded" item>
          {customList({
            title: "utelatte objekter",
            items: left,
            handleToggleAll,
            handleToggle,
            checked,
          })}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="flytt valgte til høyre"
              style={
                leftChecked.length === 0
                  ? tw`bg-gray-200 text-gray-400 w-full`
                  : tw`bg-green-200 text-green-900 w-full`
              }
            >
              &gt;&gt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={() => {
                resetSelections()
                setChecked([])
              }}
              aria-label="tilbakestill valg"
              style={tw`bg-gray-200 text-gray-900 w-full`}
            >
              Tilbakestill
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
              style={
                rightChecked.length === 0
                  ? tw`bg-gray-200 text-gray-400 w-full`
                  : tw`bg-red-200 text-red-900 w-full`
              }
            >
              &lt;&lt;
            </Button>
          </Grid>
        </Grid>
        <Grid tw="border-solid border-green-200 border-2 rounded" item>
          {customList({
            title: "valgte objekter",
            items: right,
            handleToggleAll,
            handleToggle,
            checked,
          })}
        </Grid>
      </Grid>
    </>
  )
}

const customList = ({
  title,
  items,
  handleToggleAll,
  handleToggle,
  checked,
}: {
  title: string;
  items: VegobjektType[];
  handleToggleAll: (items: VegobjektType[]) => () => void;
  handleToggle: (value: VegobjektType) => () => void;
  checked: VegobjektType[];
}) => {
  const [searchTerm, setSearchTerm] = React.useState("")

  const numberOfChecked = (items: VegobjektType[]) =>
    intersection(checked, items).length

  const classes = useStyles()

  return (
    <Card>
      <CardHeader
        // className={classes.cardHeader}
        style={tw`pb-0`}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={
              numberOfChecked(items) === items.length && items.length !== 0
            }
            indeterminate={
              numberOfChecked(items) !== items.length &&
              numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{ "aria-label": "all items selected" }}
          />
        }
        title={`${items.length} ${title}`}
        subheader={``}
      />

      <Divider />
      <div style={tw`flex items-center`}>
        <TextField
          label="Hurtigsøk"
          style={{ ...tw`mx-6 pb-2 my-0 w-full` }}
          value={searchTerm}
          onChange={event => {
            setSearchTerm(event.target.value)
          }}
        />
      </div>
      <List className={classes.list} dense component="div" role="list">
        {R.pipe(
          items,
          R.filter(objekt => {
            if (searchTerm === "") return true
            else
              return objekt.navn
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
          }),
          R.map(objekt => {
            const labelId = `transfer-list-all-item-${objekt}-label`

            return (
              <ListItem
                key={objekt.id}
                role="listitem"
                button
                onClick={() => {
                  handleToggle(objekt)()
                  setSearchTerm("")
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(objekt) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={`${objekt.navn}`} />
              </ListItem>
            )
          })
        )}
        <ListItem />
      </List>
    </Card>
  )
}
